import { format, parseISO } from 'date-fns';

/**
 * Parse reading/completed timestamps for comparisons and ranges.
 * Date-only strings (YYYY-MM-DD) use local midnight via parseISO — not UTC like `new Date(string)`.
 * @param {string|number|Date} value
 * @returns {Date|null}
 */
export const parseCompletedDate = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'string') {
        const t = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
            const d = parseISO(t);
            return isNaN(d.getTime()) ? null : d;
        }
        const d = parseISO(t);
        if (!isNaN(d.getTime())) return d;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

/**
 * Formats a date string according to the user's preferred date format
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted date string
 */
export const formatDateString = (dateString, userDateFormat) => {
    if (!dateString) return '';
    
    try {
        const date = parseISO(dateString);
        const formatMapping = {
            'MM/DD/YYYY': 'MM/dd/yyyy',
            'DD/MM/YYYY': 'dd/MM/yyyy',
            'YYYY/MM/DD': 'yyyy/MM/dd'
        };
        
        return format(date, formatMapping[userDateFormat] || 'MM/dd/yyyy');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Formats a date string according to the user's preferred date format, excluding the year
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted date string without the year
 */
export const formatDateStringWithoutYear = (dateString, userDateFormat) => {
    if (!dateString) return '';
    
    try {
        const date = parseISO(dateString);
        const formatMapping = {
            'MM/DD/YYYY': 'MM/dd',
            'DD/MM/YYYY': 'dd/MM',
            'YYYY/MM/DD': 'MM/dd'
        };
        
        return format(date, formatMapping[userDateFormat] || 'MM/dd');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Formats a date string according to the user's preferred date format, excluding the year
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted date string without the year
 */
export const formatDateStringWithoutYearAndTime = (dateString, userDateFormat) => {
    if (!dateString) return '';
    
    try {
        const date = parseISO(dateString);
        const formatMapping = {
            'MM/DD/YYYY': 'MM/dd hh:mm',
            'DD/MM/YYYY': 'dd/MM hh:mm',
            'YYYY/MM/DD': 'MM/dd hh:mm'
        };
        
        return format(date, formatMapping[userDateFormat] || 'MM/dd');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Formats a date string with full month name (e.g., "October 15, 2023")
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted date string with full month name
 */
export const formatDateWithFullMonth = (dateString, userDateFormat) => {
    if (!dateString) return '';
    
    try {
        const date = parseISO(dateString);
        const formatMapping = {
            'MM/DD/YYYY': 'MMMM d, yyyy',
            'DD/MM/YYYY': 'd MMMM, yyyy',
            'YYYY/MM/DD': 'MMMM d, yyyy'
        };
        
        return format(date, formatMapping[userDateFormat] || 'MMMM d, yyyy');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Formats a date string with full month name and time (e.g., "October 15, 2023 at 10:40pm")
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted date string with full month name and time
 */
export const formatDateWithFullMonthAndTime = (dateString, userDateFormat) => {
    if (!dateString) return '';
    
    try {
        const date = parseISO(dateString);
        const formatMapping = {
            'MM/DD/YYYY': 'MMMM d, yyyy \'at\' h:mma',
            'DD/MM/YYYY': 'd MMMM, yyyy \'at\' h:mma',
            'YYYY/MM/DD': 'MMMM d, yyyy \'at\' h:mma'
        };
        
        return format(date, formatMapping[userDateFormat] || 'MMMM d, yyyy \'at\' h:mma');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Formats a date string with full date and time (e.g., "03/12/25 at 10:30am")
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted date string with full date and time
 */
export const formatDateWithTime = (dateString, userDateFormat) => {
    if (!dateString) return '';
    
    try {
        const date = parseISO(dateString);
        const formatMapping = {
            'MM/DD/YYYY': 'MM/dd/yy \'at\' h:mma',
            'DD/MM/YYYY': 'dd/MM/yy \'at\' h:mma',
            'YYYY/MM/DD': 'yy/MM/dd \'at\' h:mma'
        };
        
        return format(date, formatMapping[userDateFormat] || 'MM/dd/yy \'at\' h:mma');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Formats a date string to show only the full month name and day (e.g., "October 15" or "15 October" based on format)
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted date string with full month name and day
 */
export const formatMonthDay = (dateString, userDateFormat) => {
    if (!dateString) return '';
    try {
        let date;
        if (typeof dateString === 'string') {
            // Try to parse ISO string first
            date = parseISO(dateString);
            // If parsing fails, try creating a new Date object
            if (isNaN(date.getTime())) {
                date = new Date(dateString);
            }
        } else {
            date = dateString;
        }
        
        if (isNaN(date.getTime())) {
            throw new Error('Invalid date');
        }
        
        const month = date.toLocaleString('en-US', { month: 'long' });
        const day = date.getDate();
        
        // Format based on user preference
        if (userDateFormat === 'DD/MM/YYYY') {
            return `${day} ${month}`;
        }
        return `${month} ${day}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
}; 

/**
 * Formats time
 * @param {string} dateString - The date string to format
 * @returns {string} - The formatted time string
 */
export const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
        const date = parseISO(dateString);
        return format(date, 'h:mm a');
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};

/**
 * Formats a date string according to the user's preferred date format and the time of the date string
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted time string
 */
export const formatTimeAndFullMonth = (dateString, userDateFormat) => {
    if (!dateString) return '';
    try {
        const formatMapping = {
            'MM/DD/YYYY': 'MM/dd/yy \'at\' h:mma',
            'DD/MM/YYYY': 'dd/MM/yy \'at\' h:mma',
            'YYYY/MM/DD': 'yy/MM/dd \'at\' h:mma'
        };
        const date = parseISO(dateString);

        const dateObj = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        if (dateObj.toDateString() === today.toDateString()) {
            return format(date, 'h:mma');
        } else if (dateObj.toDateString() === yesterday.toDateString()) {
            return format(date, '\'Yesterday at\' h:mm a');
        } else {
            return format(date, formatMapping[userDateFormat] || 'MM/dd/yy \'at\' h:mma');
        }
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
}


/**
 * Formats a date string according to the user's preferred date format and the time of the date string
 * @param {string} dateString - The date string to format
 * @param {string} userDateFormat - The user's preferred date format ('MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY/MM/DD')
 * @returns {string} - The formatted time string
 */
export const getGraphXValue = (dateString, userDateFormat, groupedByAvg, readingInterval) => {
    if (!dateString) return '';
    try {
        const completedDate = new Date(dateString);
        const formattedDate = formatDateStringWithoutYear(completedDate.toISOString(), userDateFormat);
        const hour = String(completedDate.getHours()).padStart(2, '0');
        const minute = String(completedDate.getMinutes()).padStart(2, '0');
        
        // Use formatted date for groupKey
        const groupKey = (groupedByAvg && readingInterval === 'Day') ? 
            formattedDate
            : `${formattedDate}-${hour}:${minute}`;
        return groupKey;
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
}